import { ReactNode, useEffect, useRef, useState } from "react";

type RevealDirection = "bottom" | "left" | "right" | "scale";

interface RevealProps {
  children: ReactNode;
  delay?: number;
  className?: string;
  from?: RevealDirection;
}

const hiddenTransform: Record<RevealDirection, string> = {
  bottom: "translate-y-10",
  left: "-translate-x-10",
  right: "translate-x-10",
  scale: "scale-95",
};

const Reveal = ({ children, delay = 0, className = "", from = "bottom" }: RevealProps) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (prefersReducedMotion.matches || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -60px 0px" },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const visibilityClasses = isVisible
    ? "opacity-100 translate-x-0 translate-y-0 scale-100"
    : `opacity-0 ${hiddenTransform[from]}`;

  return (
    <div
      ref={elementRef}
      style={{ transitionDelay: `${delay}ms` }}
      className={`transition-all duration-700 ease-out motion-reduce:transition-none
        ${visibilityClasses} ${className}`}
    >
      {children}
    </div>
  );
};

export default Reveal;
